/**
 * Lo elegido y lo escrito en veltica.com con «Diseña esta página», de camino al
 * asistente del panel.
 *
 * Allí el diseño vive sobre la página publicada (`designLauncher` en el
 * worker): una combinación, una paleta y una letra por su id, y los textos
 * escritos con doble clic por el id de su nodo. veltica.com es otro origen y no
 * comparte almacenamiento con el panel, así que viaja empaquetado —en la
 * dirección o en un mensaje a la ventana de acceso (`EmbedAuth`)—, se guarda
 * aquí, y al entrar el panel arma el proyecto con la portada de verdad y eso
 * encima (ver `Home`).
 */
import { FONT_PAIRS, STYLE_PALETTE_PREFIX } from '@webbuilder/theme'
import { brandPaletteOf, themeForRecipe, type CreateForm } from './preview/compose'
import type { ElementNode } from './preview/types'

/**
 * `a`: las secciones agregadas con el «+», cada una con el id de la que tiene delante (vacío: la primera).
 * `i`: las fotos cambiadas, por el id de su nodo (una imagen, o la caja que la lleva de fondo).
 * `o`: el orden en que quedaron las secciones.
 * En `t`, además de los textos por nodo, las filas de un bloque de preguntas (`<id>:q|a:<fila>`).
 */
export type SiteDesign = { c?: { s?: string; p?: string; f?: string }; t?: Record<string, string>; a?: { id?: string; after?: string }[]; i?: Record<string, string>; o?: string[]; x?: unknown }

/**
 * El paquete más grande que se acepta (base64). El guion de veltica.com tira
 * los textos antes de pasarse (`MAX_PACKED`); esto es la misma red aquí, para
 * uno armado a mano.
 */
export const MAX_SITE_DESIGN = 300_000

/**
 * El ejemplo de la IA de veltica.com con que se trabajó la página («Café
 * Brasa»): su nombre, el tipo de negocio y lo que quiere del sitio (la
 * bienvenida), la descripción para la caja de IA, lo que vende —llega al
 * proyecto «Por completar», sin precio— y qué botones llevan a su tienda.
 * Viene del cliente: se valida todo y lo que no encaja se deja fuera.
 */
export type SiteExample = {
  id: string
  name: string
  business: string
  goals: string[]
  prompt: string
  shop: { kind: 'products' | 'services'; title: string; description: string; monthly: boolean; image: string }[]
  links: Record<string, '/products' | '/services'>
}

export function siteExample(design: SiteDesign | null | undefined): SiteExample | null {
  const raw = design?.x
  if (!raw || typeof raw !== 'object') return null
  const x = raw as Record<string, unknown>
  const str = (value: unknown, max: number) => (typeof value === 'string' ? value.trim().slice(0, max) : '')
  const id = str(x.id, 40)
  const name = str(x.n, 80)
  if (!/^[a-z0-9-]+$/.test(id) || !name) return null
  const shop = (Array.isArray(x.s) ? x.s : []).slice(0, 8).flatMap((item: unknown) => {
    if (!item || typeof item !== 'object') return []
    const entry = item as Record<string, unknown>
    const kind: 'products' | 'services' | null = entry.k === 'products' || entry.k === 'services' ? entry.k : null
    const title = str(entry.t, 120)
    const image = str(entry.img, 600)
    if (!kind || !title) return []
    return [{ kind, title, description: str(entry.d, 600), monthly: entry.m === 1, image: image.startsWith(GALLERY_ORIGIN) ? image : '' }]
  })
  const links: SiteExample['links'] = {}
  if (x.l && typeof x.l === 'object') {
    for (const [node, path] of Object.entries(x.l as Record<string, unknown>).slice(0, 20)) {
      if (NODE_ID.test(node) && (path === '/products' || path === '/services')) links[node] = path
    }
  }
  return {
    id,
    name,
    business: str(x.b, 40),
    goals: (Array.isArray(x.g) ? x.g : []).filter((goal): goal is string => typeof goal === 'string').slice(0, 5),
    prompt: str(x.p, 1200),
    shop,
    links,
  }
}

/** Lo que vende el ejemplo, como la seed de la IA (`aiGeneration.seed` → `seedAiCatalog` en el worker). */
function exampleSeed(example: SiteExample) {
  const item = (entry: SiteExample['shop'][number]) => ({
    title: entry.title,
    description: entry.description,
    ...(entry.image ? { image: entry.image } : {}),
  })
  return {
    services: example.shop.filter((entry) => entry.kind === 'services').slice(0, 3).map(item),
    digital: [],
    products: example.shop.filter((entry) => entry.kind === 'products').slice(0, 3).map(item),
    bookings: [],
    posts: [],
  }
}

/** Los botones del ejemplo, enlazados a la página de su tienda. */
function withExampleLinks(nodes: ElementNode[], links: SiteExample['links']): ElementNode[] {
  return nodes.map((node) => {
    const path = links[node.id]
    const children = node.children?.length ? withExampleLinks(node.children, links) : node.children
    if (!path && children === node.children) return node
    return {
      ...node,
      ...(path ? { interaction: { ...node.interaction, action: 'link', href: path } } : {}),
      children: children ?? [],
    } as ElementNode
  })
}

/** De base64url a lo elegido; `null` si no se entiende. */
export function unpackSiteDesign(packed: string | null | undefined): SiteDesign | null {
  if (!packed) return null
  try {
    const json = decodeURIComponent(escape(atob(packed.replace(/-/g, '+').replace(/_/g, '/'))))
    const parsed = JSON.parse(json) as SiteDesign
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

/** La receta de lo elegido, la misma que calcula el worker (`recipeOf` en `liveDesign`). */
export function siteRecipe(choice: NonNullable<SiteDesign['c']>): CreateForm['recipe'] | null {
  const id = (value: unknown) => (typeof value === 'string' && /^[a-z0-9|:_-]{1,80}$/i.test(value) ? value : undefined)
  const s = id(choice.s), p = id(choice.p), f = id(choice.f)
  if (!s && !p && !f) return null
  return {
    ...(s ? { designStyleId: s, paletteId: p ?? STYLE_PALETTE_PREFIX + s } : p ? { paletteId: p } : {}),
    ...(f ? { fontPairId: f } : {}),
  }
}

/**
 * La portada con las secciones agregadas, cada una detrás de la que tenía
 * delante en la página; si ésa ya no está, al final (antes no hay pie aquí: la
 * barra y el pie los pone el sitio, no son nodos de la página).
 */
function withChildren(node: ElementNode): ElementNode {
  return { ...node, children: (node.children ?? []).map(withChildren) }
}

function withAddedSections(elements: ElementNode[], added: SiteDesign['a'], available: ElementNode[]): ElementNode[] {
  const out = [...elements]
  for (const entry of (added ?? []).slice(0, 20)) {
    const found = available.find((item) => item.id === entry?.id)
    if (!found || out.some((item) => item.id === found.id)) continue
    /* Con su lista de hijos siempre presente, como el resto de la página (ver `hydrateFromTemplate`). */
    const node = withChildren(found)
    const at = entry?.after ? out.findIndex((item) => item.id === entry.after) : -1
    out.splice(at >= 0 ? at + 1 : out.length, 0, node)
  }
  return out
}

const titleCase = (id: string) => id.replace(/[-_|:]+/g, ' ').trim().replace(/\b[a-z]/g, (letter) => letter.toUpperCase())

/** Lo elegido, dicho en una línea: «Creator · Ocean · Fraunces». Vacío si no se eligió estilo. */
export function siteDesignLabel(design: SiteDesign): string {
  const recipe = design.c ? siteRecipe(design.c) : null
  if (!recipe) return ''
  const parts: string[] = []
  if (recipe.designStyleId) parts.push(titleCase(recipe.designStyleId))
  /* La paleta, si no es la de la propia combinación: una suelta, o la de OTRA combinación. */
  if (recipe.paletteId && recipe.paletteId !== STYLE_PALETTE_PREFIX + (recipe.designStyleId ?? '')) {
    parts.push(titleCase(recipe.paletteId.startsWith(STYLE_PALETTE_PREFIX) ? recipe.paletteId.slice(STYLE_PALETTE_PREFIX.length) : recipe.paletteId))
  }
  const pair = recipe.fontPairId ? FONT_PAIRS.find((item) => item.id === recipe.fontPairId) : null
  if (pair) parts.push(pair.heading)
  return parts.join(' · ')
}

/** Si además de elegir estilo se trabajó la página: textos, fotos, secciones agregadas u orden. */
export function siteDesignHasContent(design: SiteDesign): boolean {
  const some = (value: unknown) => Boolean(value && typeof value === 'object' && Object.keys(value).length)
  return some(design.t) || some(design.i) || some(design.a) || some(design.o)
}

/**
 * El estilo que se trae, puesto sobre un formulario recién armado —el que dejó
 * la IA o el de un template—: quien eligió una combinación en veltica.com la
 * conserva empiece por donde empiece. Con una combinación entera, sustituye;
 * con sólo colores o sólo letra, se cambia eso y lo demás se queda.
 */
export function withStartRecipe(form: CreateForm, start: CreateForm['recipe'] | null | undefined): CreateForm {
  if (!start || !Object.keys(start).length) return form
  const recipe = start.designStyleId ? start : { ...form.recipe, ...start }
  return {
    ...form,
    recipe,
    ...(start.designStyleId ? { combination: start.designStyleId } : {}),
    theme: themeForRecipe(form, recipe),
    brandPalette: brandPaletteOf(recipe),
  }
}

/**
 * Sin la caja de IA: en veltica.com esa caja crea sitios en Véltica, y en el
 * sitio de quien se lleva la página no pinta nada. Es un incrustado que apunta
 * a `/embed/prompt` del panel; se quita él y, si su caja se queda vacía, la caja.
 */
function withoutPromptBox(nodes: ElementNode[]): ElementNode[] {
  const isBox = (node: ElementNode) => Object.entries(node).some(([key, value]) => key !== 'children' && typeof value === 'string' && value.includes('/embed/prompt'))
  let changed = false
  const out: ElementNode[] = []
  for (const node of nodes) {
    if (isBox(node)) { changed = true; continue }
    const had = node.children?.length ?? 0
    const children = had ? withoutPromptBox(node.children) : node.children
    if (children === node.children) { out.push(node); continue }
    changed = true
    if (had && !children.length) continue
    out.push({ ...node, children })
  }
  return changed ? out : nodes
}

/** La galería del modo diseño sólo ofrece fotos de la casa (ver `handleDesignImages` en el worker). */
const GALLERY_ORIGIN = 'https://assets.veltica.com/'
const NODE_ID = /^[0-9a-f-]{36}$/i

/** Lo escrito en una fila de un bloque de preguntas: `<id del bloque>:q|a:<fila>`. */
type FaqEdits = Map<string, { row: number; field: 'question' | 'answer'; text: string }[]>
type FaqBehavior = { type?: string; items?: Record<string, unknown>[] }

/**
 * Las fotos cambiadas y las preguntas escritas, en su nodo. Una foto es el
 * `src` de una imagen o la del `background-image` de una caja. Lo que no cambia
 * se queda tal cual, el mismo objeto.
 */
function withNodeEdits(nodes: ElementNode[], photos: Map<string, string>, faq: FaqEdits): ElementNode[] {
  return nodes.map((node) => {
    const patch: Record<string, unknown> = {}
    const photo = photos.get(node.id)
    if (photo && node.type === 'image') patch.src = photo
    const background = (node.styles as { desktop?: Record<string, unknown> } | undefined)?.desktop?.backgroundImage
    if (photo && node.type !== 'image' && typeof background === 'string' && background.includes('url(')) {
      patch.styles = { ...node.styles, desktop: { ...(node.styles as { desktop?: object }).desktop, backgroundImage: background.replace(/url\([^)]*\)/, `url("${photo}")`) } }
    }
    const rows = faq.get(node.id)
    const behavior = (node as { behavior?: FaqBehavior }).behavior
    if (rows && behavior?.type === 'faq' && Array.isArray(behavior.items)) {
      const items = behavior.items.map((item, index) => {
        const mine = rows.filter((entry) => entry.row === index)
        return mine.length ? { ...item, ...Object.fromEntries(mine.map((entry) => [entry.field, entry.text])) } : item
      })
      patch.behavior = { ...behavior, items }
    }
    const children = node.children?.length ? withNodeEdits(node.children, photos, faq) : node.children
    const sameChildren = children === node.children || children.every((child, index) => child === node.children[index])
    return sameChildren && !Object.keys(patch).length ? node : { ...node, ...patch, children: children ?? [] } as ElementNode
  })
}

/** Las secciones en el orden en que se dejaron: las nombradas ocupan, en ese orden, los sitios que ya tenían. */
function withOrder(elements: ElementNode[], order: unknown): ElementNode[] {
  const ids = Array.isArray(order) ? order.filter((id): id is string => typeof id === 'string').slice(0, 60) : []
  const rank = new Map(ids.map((id, index) => [id, index]))
  const named = elements.filter((node) => rank.has(node.id)).sort((a, b) => rank.get(a.id)! - rank.get(b.id)!)
  if (named.length < 2) return elements
  let next = 0
  return elements.map((node) => (rank.has(node.id) ? named[next++]! : node))
}

/** El formulario de la portada con lo que se eligió, se escribió, se cambió y se agregó sobre ella. */
export function withSiteDesign(form: CreateForm, design: SiteDesign, sections: unknown[] = []): CreateForm {
  const photos = new Map(Object.entries(design.i ?? {})
    .filter(([id, url]) => NODE_ID.test(id) && typeof url === 'string' && url.startsWith(GALLERY_ORIGIN) && /^[\w\-.:/%?=&]{1,600}$/.test(url)))
  const faq: FaqEdits = new Map()
  for (const [key, text] of Object.entries(design.t ?? {})) {
    const match = /^([0-9a-f-]{36}):(q|a):(\d{1,3})$/i.exec(key)
    if (!match || typeof text !== 'string') continue
    faq.set(match[1]!, [...(faq.get(match[1]!) ?? []), { row: Number(match[3]), field: match[2] === 'q' ? 'question' : 'answer', text: text.slice(0, 2000) }])
  }
  const example = siteExample(design)
  const ordered = withoutPromptBox(withOrder(withAddedSections(form.baseElements, design.a, sections as ElementNode[]), design.o))
  const placed = example && Object.keys(example.links).length ? withExampleLinks(ordered, example.links) : ordered
  const elements = photos.size || faq.size ? withNodeEdits(placed, photos, faq) : placed
  if (elements.length !== form.baseElements.length || elements.some((node, index) => node !== form.baseElements[index])) {
    const page = form.base.pages[0] ?? {}
    form = { ...form, baseElements: elements, base: { ...form.base, pages: [{ ...page, elements }, ...form.base.pages.slice(1)] } }
  }
  const recipe = design.c ? siteRecipe(design.c) : null
  const texts = Object.fromEntries(Object.entries(design.t ?? {})
    .filter(([id, text]) => NODE_ID.test(id) && typeof text === 'string')
    .map(([id, text]) => [id, text.slice(0, 2000)]))
  /* Del ejemplo: su nombre y, «Por completar», lo que vende. */
  if (example) {
    /* La seed no está en el tipo del borrador (la de la IA la lee y la quita el
       worker al crear); va en los datos tal cual, que es como llega allí. */
    const base = form.base as CreateForm['base'] & { aiGeneration?: Record<string, unknown> }
    form = {
      ...form,
      companyName: example.name,
      base: { ...base, aiGeneration: { ...base.aiGeneration, seed: exampleSeed(example) } } as CreateForm['base'],
    }
  }
  return {
    ...form,
    ...(recipe ? { recipe, theme: themeForRecipe(form, recipe), brandPalette: brandPaletteOf(recipe) } : {}),
    textEdits: { ...form.textEdits, ...texts },
  }
}

/** Un día, como lo escrito en la caja de IA (`pendingPrompt`). */
const MAX_AGE_MS = 24 * 60 * 60 * 1000
const KEY = 'veltica:pending-site-design'

/** Guarda lo elegido, empaquetado, hasta que haya sesión. `false` si no se pudo. */
export function savePendingSiteDesign(packed: string): boolean {
  if (!unpackSiteDesign(packed)) return false
  try {
    globalThis.localStorage.setItem(KEY, JSON.stringify({ packed, savedAt: Date.now() }))
    return true
  } catch {
    return false
  }
}

/** Lo guardado, sin borrarlo: sigue ahí mientras se elige por dónde empezar, aunque se recargue. */
export function peekPendingSiteDesign(): SiteDesign | null {
  try {
    const raw = globalThis.localStorage.getItem(KEY)
    if (!raw) return null
    const saved = JSON.parse(raw) as { packed?: unknown; savedAt?: unknown }
    if (typeof saved.packed !== 'string' || typeof saved.savedAt !== 'number' || Date.now() - saved.savedAt > MAX_AGE_MS) return null
    return unpackSiteDesign(saved.packed)
  } catch {
    return null
  }
}

/**
 * Si lo guardado acaba de llegar y aún no se ha enseñado la pantalla de
 * empezar por ello. Al preguntarlo queda apuntado: la pantalla se abre sola
 * UNA vez, al llegar de veltica.com, y no cada vez que se entra al panel
 * mientras lo guardado siga ahí.
 */
export function takePendingSiteDesignArrival(): boolean {
  try {
    const raw = globalThis.localStorage.getItem(KEY)
    if (!raw) return false
    const saved = JSON.parse(raw) as { packed?: unknown; savedAt?: unknown; seen?: unknown }
    if (saved.seen) return false
    globalThis.localStorage.setItem(KEY, JSON.stringify({ ...saved, seen: true }))
    return typeof saved.savedAt === 'number' && Date.now() - saved.savedAt <= MAX_AGE_MS
  } catch {
    return false
  }
}

/** Ya se usó —se creó el proyecto— o se descartó. */
export function clearPendingSiteDesign(): void {
  try { globalThis.localStorage.removeItem(KEY) } catch { /* sin almacenamiento no hay nada que borrar */ }
}

/** Lo guardado, y se borra: abrir el asistente una vez es lo que se pidió. */
export function consumePendingSiteDesign(): SiteDesign | null {
  try {
    const raw = globalThis.localStorage.getItem(KEY)
    if (!raw) return null
    globalThis.localStorage.removeItem(KEY)
    const saved = JSON.parse(raw) as { packed?: unknown; savedAt?: unknown }
    if (typeof saved.packed !== 'string' || typeof saved.savedAt !== 'number' || Date.now() - saved.savedAt > MAX_AGE_MS) return null
    return unpackSiteDesign(saved.packed)
  } catch {
    return null
  }
}
